/**
 * YouTube IFrame API 的載入器與型別，由 outreach 示範與 /transcription 字幕示範共用。
 *
 * script 只載一次：`youtubeApiPromise` 是模組層的單例，兩個示範同時出現在同一次
 * 造訪時共用同一份。各自持有一份 loader 就會各注入一支 `iframe_api`，第二支的
 * `onYouTubeIframeAPIReady` 會覆蓋第一支，先掛上的那個示範永遠等不到 resolve。
 *
 * 這裡只負責「把 API 弄進來」與「用它開一個播放器」，不碰畫面：要不要顯示原生
 * 控制列、失敗時退回什麼，是各個使用端自己的決定。
 */

export interface YouTubePlayer {
  getCurrentTime(): number
  seekTo(seconds: number, allowSeekAhead?: boolean): void
  playVideo(): void
  pauseVideo(): void
  getPlaybackRate(): number
  setPlaybackRate(rate: number): void
  destroy(): void
}

/**
 * 用得到的 `playerVars`。刻意逐一列出而不是 `Record<string, string | number>`：
 * 拼錯的參數 YouTube 會安靜忽略，型別是唯一擋得住的地方。
 */
export interface YouTubePlayerVars {
  autoplay?: 0 | 1
  controls?: 0 | 1
  /** 起播秒數。 */
  start?: number
  playsinline?: 0 | 1
  rel?: 0 | 1
  disablekb?: 0 | 1
  fs?: 0 | 1
  modestbranding?: 0 | 1
  iv_load_policy?: 1 | 3
  origin?: string
}

export interface YouTubePlayerOptions {
  videoId: string
  /** 預設用 `https://www.youtube-nocookie.com`（privacy-enhanced）。 */
  host?: string
  playerVars: YouTubePlayerVars
  events: {
    onReady?: () => void
    onStateChange?: (event: { data: number }) => void
    onError?: (event: { data: number }) => void
  }
}

export interface YouTubeWindow extends Window {
  YT?: {
    Player: new (element: HTMLElement, options: YouTubePlayerOptions) => YouTubePlayer
  }
  onYouTubeIframeAPIReady?: () => void
}

let youtubeApiPromise: Promise<void> | null = null

/** 注入 `iframe_api` 並等 YouTube 回呼；已經載好就直接 resolve。 */
export function loadYouTubeApi(): Promise<void> {
  if (typeof window === 'undefined') return Promise.reject(new Error('browser required'))
  const youtubeWindow = window as YouTubeWindow
  if (youtubeWindow.YT?.Player) return Promise.resolve()
  if (youtubeApiPromise) return youtubeApiPromise
  youtubeApiPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = 'https://www.youtube.com/iframe_api'
    script.async = true
    script.onerror = () => reject(new Error('YouTube player failed to load'))
    youtubeWindow.onYouTubeIframeAPIReady = () => resolve()
    document.head.appendChild(script)
  })
  return youtubeApiPromise
}

/**
 * 在 `element` 上開一個播放器。**`element` 會被 YouTube 換成 iframe**，所以它必須
 * 是一個專門給播放器用的空節點，而且在元件的生命週期裡不能被條件式地拿掉——React
 * 不知道那個節點已經不在了。
 *
 * API 不在（載入被擋掉、或 resolve 後 `window.YT` 仍然是空的）就丟出來，讓使用端
 * 走自己的退回路徑。
 */
export function createYouTubePlayer(
  element: HTMLElement,
  options: YouTubePlayerOptions
): YouTubePlayer {
  const youtubeWindow = window as YouTubeWindow
  if (!youtubeWindow.YT?.Player) throw new Error('YouTube API unavailable')
  return new youtubeWindow.YT.Player(element, options)
}
